/* istanbul ignore file */

import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { BrannanNavigation } from '@graphql/generated-base';
import type { BoxProps } from '@withjoy/joykit';
import { Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import React, { useMemo } from 'react';
import { NavLink as RouterLink } from '@react-router';
import { getHoverColor, isSupportedEventPage } from '../../../layout.utils';
import { StyledLegacyNavLinkContainer } from './NavBar.styles';
import { TopBarNavEventMenu } from '@apps/guest/packages/layout-engine/components/TopBarNavEventMenu';
import { PreviewDisabledTooltipContent } from '@apps/guest/packages/layout-engine/components/PreviewDisabledTooltipContent/PreviewDisabledTooltipContent';
import { usePreviewIframeScale } from '@apps/guest/packages/layout-engine/components/PreviewDisabledTooltipContent/usePreviewIframeScale';
import type { NavbarEventPropsInterface } from '../../../layout.types';
import { formatPagesForBrannan } from './NavBar.utils';
import { getNativeRegistryHardNavUrl } from '../../../layout.utils';
import { isInIframe } from '@shared/utils/isInIframe';
import { useEventPageRoute } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import useQueryString from '@shared/utils/hooks/useQueryString';
import { useDefaultPage } from '@shared/hooks/useDefaultPage';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body1ToNavigationOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { pxToRem } from '@withjoy/joykit/theme';
import { useGuestSiteNavUtm } from '@apps/guest/packages/layout-engine/common/utils/useGuestSiteNavUtm';
import type { WritableUtmParameters } from '@shared/utils/utmParameters';
import { applyUtmParametersToUrl } from '@shared/utils/utmParameters';

export interface NavBarProps
  extends Readonly<{
      eventHandle: string;
      pages: ReadonlyArray<EventPageFragment>;
      variant: 'inline' | 'menu';
      title?: Maybe<string>;
      isCustomPage?: boolean;
      handleMenuClicked?: (item: string, utm?: WritableUtmParameters) => void;
      textColor?: string;
      /**
       * When true, the nav will expand to full width.
       * Used in StickyNavBar component
       */
      expandToFullWidth?: boolean;
      /**
       * When true, the nav will not expand to full width even if the page blends seamlessly with the art background.
       * Used in EventNameNavForStandaloneHeader component
       */
      preventExpandingToFullWidth?: boolean;
    }>,
    Omit<BoxProps, 'title'> {}

const LEGACY_PAGE_SLUGS = new Set(['rsvp', 'moments']);

const LegacyNavLink: React.FC<{
  to: string;
  isRsvp: boolean;
  isNativeRegistry: boolean;
  isLegacyPage: boolean;
  isPreviewing: boolean;
  previewScale?: number;
  onClick: () => void;
  colorOverride?: string;
  isDefaultPage?: boolean;
}> = ({ to, isRsvp, isNativeRegistry, isLegacyPage, isPreviewing, previewScale, children, onClick, colorOverride, isDefaultPage, ...rest }) => {
  const { pageFontColor } = useColorPaletteProvider();
  const linkColor = useMemo(() => colorOverride || pageFontColor, [colorOverride, pageFontColor]);
  const linkHoverColor = useMemo(() => (linkColor ? getHoverColor(linkColor) : undefined), [linkColor]);

  if (isLegacyPage && isPreviewing) {
    return (
      <StyledLegacyNavLinkContainer
        _empty={{ display: 'none' }}
        paddingX={5}
        linkColor={linkColor}
        linkHoverColor={linkHoverColor}
        data-testid={(rest as Record<string, string>)['data-testid']}
      >
        <TooltipV2
          onMouseEnterDelay={500}
          content={
            <PreviewDisabledTooltipContent
              href={to}
              previewScale={previewScale}
            />
          }
        >
          <TextV2
            cursor="default"
            color={linkColor}
          >
            {children}
          </TextV2>
        </TooltipV2>
      </StyledLegacyNavLinkContainer>
    );
  }

  return (
    <StyledLegacyNavLinkContainer
      _empty={{ display: 'none' }}
      paddingX={5}
      linkColor={linkColor}
      linkHoverColor={linkHoverColor}
    >
      {isRsvp ? (
        <a href={to}>{children}</a>
      ) : isNativeRegistry ? (
        <a
          onClick={onClick}
          href={to}
          data-testid={(rest as Record<string, string>)['data-testid']}
        >
          {children}
        </a>
      ) : (
        <RouterLink
          onClick={onClick}
          data-testid={(rest as Record<string, string>)['data-testid']}
          to={to}
          exact={isDefaultPage}
        >
          {children}
        </RouterLink>
      )}
    </StyledLegacyNavLinkContainer>
  );
};

const OVERFLOW_BOUND = 860;
const DEFAULT_PADDING = pxToRem(12);
const FULL_WIDTH = '100vw';
const FULL_WIDTH_PADDING = pxToRem(64);

const LegacyBrannanOverflowNav: React.FC<Omit<NavBarProps, 'variant' | 'title'> & { isSinglePage: boolean }> = ({
  pages,
  eventHandle,
  handleMenuClicked,
  backgroundColor,
  textColor,
  paddingTop = 8,
  borderRadius = 0,
  backdropFilter = 'none',
  border = 'none',
  boxShadow,
  expandToFullWidth,
  preventExpandingToFullWidth,
  isSinglePage,
  ...restProps
}) => {
  const eventPageRoute = useEventPageRoute(eventHandle, pages);
  const resolveNavUtm = useGuestSiteNavUtm('nav_menu');
  const queryString = useQueryString();
  const [useRegistryLinksNav, isRegistryLinksNavLoading] = useIsFeatureEnabled('registryEnableGuestNavRegistryLinks');
  const { isDefaultPage } = useDefaultPage(pages);
  const { pageColor, artBackgroundColor, brannanInnerFrameBorder, themeId, isCustomArtApplied } = useColorPaletteProvider();

  // Expand nav to full viewport width (FULL_WIDTH) with FULL_WIDTH_PADDING padding when:
  const shouldExpandNav = useMemo(() => {
    if (preventExpandingToFullWidth) {
      return false;
    }

    // When the expand to full width flag is enabled (used in the StickyNavBar component)
    if (expandToFullWidth) {
      return true;
    }

    // When the page blends seamlessly with the art background (matching colors, blank theme, no shadow).
    // Custom theme might have colors and may not blend well.
    return pageColor === artBackgroundColor && themeId === 'blank' && !brannanInnerFrameBorder && !isCustomArtApplied;
  }, [pageColor, artBackgroundColor, themeId, brannanInnerFrameBorder, expandToFullWidth, preventExpandingToFullWidth, isCustomArtApplied]);

  const isPreviewing = isInIframe();
  const previewScale = usePreviewIframeScale(isPreviewing);

  return (
    <Flex
      as="nav"
      {...restProps}
      justifyContent="center"
      paddingTop={paddingTop}
      pointerEvents={isPreviewing ? 'all' : 'unset'}
    >
      <Flex
        as="ul"
        flexWrap={'wrap'}
        // CSS replaces the old post-mount JS overflow measure (860px cap, widened to 90% after
        // hydration) — identical final width, but correct from SSR/first paint, so the nav no
        // longer collapses from two wrapped rows to one and shifts the whole page up (CLS).
        maxWidth={shouldExpandNav ? FULL_WIDTH : `max(${OVERFLOW_BOUND}px, 90%)`}
        width={shouldExpandNav ? FULL_WIDTH : undefined}
        minWidth={shouldExpandNav ? FULL_WIDTH : undefined}
        justifyContent="center"
        backgroundColor={backgroundColor}
        borderRadius={borderRadius}
        border={border}
        backdropFilter={backdropFilter}
        boxShadow={boxShadow}
        paddingX={shouldExpandNav ? FULL_WIDTH_PADDING : DEFAULT_PADDING}
      >
        {pages.map(page => {
          if (!isSupportedEventPage(page.type)) {
            return null;
          }

          const isRsvp = page.pageSlug === 'rsvp';
          // While the remote flag is resolving, bias toward the SPA path (skip the hard-nav
          // helper) so a fast click on treatment users doesn't land them in /registry.
          const registryHardNavUrl =
            useRegistryLinksNav || isRegistryLinksNavLoading
              ? undefined
              : getNativeRegistryHardNavUrl(page.type, page.pageSlug, page.pageKind, isSinglePage, eventHandle, queryString);
          const isNativeRegistry = !!registryHardNavUrl;
          const isLegacyPage = LEGACY_PAGE_SLUGS.has(page.pageSlug);
          const navUtm = resolveNavUtm(page.pageSlug);
          const to = applyUtmParametersToUrl(registryHardNavUrl ?? eventPageRoute.goToPath(page.type, page.pageSlug), navUtm);

          return (
            <LegacyNavLink
              onClick={() => handleMenuClicked && handleMenuClicked(page.pageSlug, navUtm)}
              key={page.id}
              data-testid={`nav-${page.id}`}
              isLegacyPage={isLegacyPage}
              isPreviewing={isPreviewing}
              previewScale={previewScale}
              isRsvp={isRsvp}
              isNativeRegistry={isNativeRegistry}
              to={to}
              colorOverride={textColor}
              isDefaultPage={isDefaultPage(page.pageSlug, page.type)}
            >
              <GuestSiteTypographyOverride override={body1ToNavigationOverride}>
                <TextV2
                  typographyVariant="body1"
                  paddingTop={5}
                  paddingBottom={5}
                >
                  {page.pageTitle}
                </TextV2>
              </GuestSiteTypographyOverride>
            </LegacyNavLink>
          );
        })}
      </Flex>
    </Flex>
  );
};

const NavBar: React.FC<NavBarProps & { eventProps: NavbarEventPropsInterface }> = ({
  eventProps,
  eventHandle,
  pages: origPages,
  variant,
  title,
  isCustomPage,
  handleMenuClicked,
  ...restProps
}) => {
  const isVariantMenu = variant === 'menu';
  const isVariantInline = variant === 'inline';
  const isSinglePage = eventProps.brannanNavigation === BrannanNavigation.singlePage;
  const pages = useMemo(() => (isSinglePage ? origPages : formatPagesForBrannan(origPages)), [origPages, isSinglePage]);
  const { pageColor } = useColorPaletteProvider();

  const eventPropsFormatted = useMemo(
    () => ({
      ...eventProps,
      pages: isSinglePage ? eventProps.pages : formatPagesForBrannan(eventProps.pages),
    }),
    [eventProps, isSinglePage],
  );

  return (
    <>
      {isVariantMenu && (
        <TopBarNavEventMenu
          handleMenuClicked={handleMenuClicked}
          eventHandle={eventHandle}
          title={title}
          eventProps={eventPropsFormatted}
          isCustomPage={isCustomPage}
          backgroundColor={pageColor}
          isSinglePage={isSinglePage}
        />
      )}
      {isVariantInline && (
        <LegacyBrannanOverflowNav
          handleMenuClicked={handleMenuClicked}
          pages={pages}
          eventHandle={eventHandle}
          isSinglePage={isSinglePage}
          {...restProps}
        />
      )}
    </>
  );
};

NavBar.displayName = 'NavBar';

export { NavBar };
